"use client";

import { useState } from "react";
import Link from "../../../components/LocaleLink";
import { useLocale } from "../../../components/LocaleProvider";

type Row = [string, string];
type Group = { title: string; items: Row[] };
type Section = { title: string; items?: Row[]; groups?: Group[] };

const BOOK_URL = "https://egodent.co.uk/book-online";
const BOOK_URL_RO = "https://egodent.co.uk/ro/book-online";
const UTM = "utm_source=site&utm_medium=cta&utm_campaign=egodent_pricelist";

/* ---------------- DENTAL SECTIONS (exact ce aveai) ---------------- */

const dentalSections: Section[] = [
  {
    title: "General",
    items: [
      [
        "New patient initial consultation (digital intraoral scan included)",
        "£95",
      ],
      ["Routine examination", "£50"],
      ["Emergency appointment (same day)", "£200"],
      ["Small X-ray", "£15"],
      ["OPG X-ray", "£85"],
      ["CT scan (single arch - partial)", "£150"],
      ["CT scan (single arch - full)", "£180"],
      ["CT scan (dual arch - full)", "£210"],
      ["Root canal treatment (single canal)", "£400"],
      ["Root canal treatment (premolar)", "£480"],
      ["Root canal treatment (molar)", "£550"],
      ["Re-RCT", "from £700"],
      ["Composite fillings", "from £150"],
      ["Ceramic fillings", "from £180"],
      ["Temporary filling", "£50"],
      ["Temporary crown", "£200"],
      ["Re-cement crown", "£100"],
      ["Glass ionomer", "£100"],
      ["Sealant (for children)", "per tooth £30"],
      ["Simple extraction", "from £200"],
      ["Surgical extraction", "from £400"],
    ],
  },

  {
    title: "Advanced Restoration",
    items: [
      ["Zirconium crown", "from £690"],
      ["Post crown (per post)", "from £180"],
      ["Zirconium bridgework (per unit)", "from £690"],
      ["Single tooth E-max veneer (inc shade)", "from £900"],
      ["E-max veneer", "from £900"],
      ["Composite onlay/inlay", "from £690"],
      ["Ceramic onlay/inlay", "from £690"],
    ],
  },

  {
    title: "Dental Implants",
    items: [
      ["Dental implant treatment planning", "from £150"],
      ["Dental implant placement", "from £1200"],
      ["Implant crown", "from £800"],
      ["Bone augmentation", "from £650"],
      ["Sinus lift", "from £1200"],
      ["Cementable implant temporary crown", "from £200"],
      ["All-on-4 Same Day Teeth", "from £5990"],
    ],
  },

  {
    title: "Cosmetics",
    items: [["Complex home tooth whitening Enlighten", "from £699"]],
  },

  {
    title: "Other",
    items: [
      ["Night guard", "£150"],
      ["Periodontal treatment", "from £250"],
      ["Hygienist treatment", "from £85"],
      ["Air Flow", "£35"],
    ],
  },

  {
    title: "Dentures",
    items: [
      ["Private full single arch", "£1000"],
      ["Private full upper and lower", "£1800"],
      ["Reline", "from £250"],
      ["Addition of tooth", "£250"],
      ["Repair", "from £250"],
    ],
  },

  {
    title: "Orthodontics 1/2",
    groups: [
      {
        title: "A. Initial steps into orthodontic treatment",
        items: [
          ["New Patient Consultation", "£150"],
          ["New Patient+ Adjustment", "£200"],
          ["Orthodontic Records Package", "£350–450"],
          ["Study models", "£100–150"],
        ],
      },
      {
        title: "B. Types of orthodontic treatments",
        items: [
          ["Braces < 12 months (all inclusive)", "£3500"],
          ["Braces 15–18 months (all inclusive)", "£4000–5000"],
          ["Braces 24–30 months (all inclusive)", "£5500–7000"],
        ],
      },
    ],
  },

  {
    title: "Orthodontics 2/2",
    groups: [
      {
        title: "2. Clear Aligners — ALL INCLUSIVE",
        items: [
          ["Minor complexity", "£2500"],
          ["Medium complexity", "£3500–4500"],
          ["Comprehensive", "over £4500"],
        ],
      },
      {
        title: "3. Treatments for children",
        items: [
          [
            "Removable appliances, expanders — ALL INCLUSIVE",
            "by assessment",
          ],
        ],
      },
      {
        title: "C. Retention and Bracket Removal (Retainers)",
        items: [
          [
            "Brace removal for non-EgoDent patients (single/dual arch)",
            "£250–500",
          ],
          ["Fixed retainers in-office, single arch", "£175"],
          ["Fixed retainers in-lab custom made, single arch", "£300"],
          ["Removable retainers (Essix) single arch", "£150"],
        ],
      },
    ],
  },

  {
    title: "Periodontal Treatments",
    items: [
      ["Initial consultation", "£155"],
      ["Deep cleaning (one session / price per arch)", "£175"],
      ["Closed debridement — Anterior (up to 6 teeth)", "£300"],
      ["Closed debridement — Posterior (up to 6 teeth)", "£250"],
      ["Helbo Photodynamic Therapy (per arch)", "£250"],
      ["Flap Surgery — Anterior (up to 6 teeth)", "£850"],
      ["Flap Surgery — Posterior (up to 6 teeth)", "£500"],
      ["Single Tooth Recession (CTG)", "£750"],
      ["Multiple Recessions", "from £950"],
      ["Frenectomy", "£400"],
      ["Crown Lengthening", "from £650"],
      ["Other Surgeries (GTR)", "from £1500"],
      ["Reassessment (after 3 months)", "£85"],
      ["Supportive Periodontal Care", "from £100"],
    ],
  },
];

/* ---------------- MEDICAL SECTIONS (DIN POZA, FĂRĂ “was”) ---------------- */

const medicalSections: Section[] = [
  {
    title: "Obstetrics & Gynaecology",
    items: [
      ["Gynaecological consultation", "£179"],
      ["Obstetrical consultation", "£129"],
      ["Transvaginal ultrasound", "£129"],
      ["Pregnancy diagnosis ultrasound", "£179"],
      ["First-trimester ultrasound", "£249"],
      ["Gender reveal ultrasound", "£179"],
      ["Fetal morphology ultrasound", "£279"],
      ["Third-trimester ultrasound", "£249"],
      ["3D/4D Ultrasound", "£329"],
    ],
  },
  {
    title: "Dermatology",
    items: [
      ["Initial consultation", "£179"],
      ["Follow-up consultation", "£139"],
    ],
  },
 
  {
    title: "Paediatrics",
    items: [
      ["Initial consultation", "£179"],
      ["Follow-up consultation", "£139"],
    ],
  },
  {
    title: "Urology",
    items: [
      ["Initial consultation", "£179"],
      ["Follow-up consultation", "£139"],
    ],
  },
  {
    title: "General Medicine",
    items: [
      ["Initial consultation", "£179"],
      ["Follow-up consultation", "£139"],
    ],
  },
];


const RO: Record<string, string> = {
  // sections
  General: "Stomatologie generală",
  "Advanced Restoration": "Restaurări avansate",
  "Dental Implants": "Implanturi dentare",
  Cosmetics: "Stomatologie estetică",
  Other: "Altele",
  Dentures: "Proteze dentare",
  "Orthodontics 1/2": "Ortodonție 1/2",
  "Orthodontics 2/2": "Ortodonție 2/2",
  "Periodontal Treatments": "Tratamente parodontale",
  "Obstetrics & Gynaecology": "Obstetrică și ginecologie",
  Dermatology: "Dermatologie",
  Paediatrics: "Pediatrie",
  Urology: "Urologie",
  "General Medicine": "Medicină generală",

  // general
  "New patient initial consultation (digital intraoral scan included)":
    "Prima consultație pentru pacient nou (include scanare intraorală digitală)",
  "Routine examination": "Examinare de rutină",
  "Emergency appointment (same day)": "Programare de urgență (în aceeași zi)",
  "Small X-ray": "Radiografie mică",
  "OPG X-ray": "Radiografie panoramică (OPG)",
  "CT scan (single arch - partial)": "CT (o arcadă – parțial)",
  "CT scan (single arch - full)": "CT (o arcadă – complet)",
  "CT scan (dual arch - full)": "CT (ambele arcade – complet)",
  "Root canal treatment (single canal)": "Tratament de canal (un canal)",
  "Root canal treatment (premolar)": "Tratament de canal (premolar)",
  "Root canal treatment (molar)": "Tratament de canal (molar)",
  "Re-RCT": "Retratament de canal",
  "Composite fillings": "Obturații compozite",
  "Ceramic fillings": "Obturații ceramice",
  "Temporary filling": "Obturație provizorie",
  "Temporary crown": "Coroană provizorie",
  "Re-cement crown": "Recimentare coroană",
  "Glass ionomer": "Ionomer de sticlă",
  "Sealant (for children)": "Sigilare (pentru copii)",
  "Simple extraction": "Extracție simplă",
  "Surgical extraction": "Extracție chirurgicală",

  // advanced restoration
  "Zirconium crown": "Coroană din zirconiu",
  "Post crown (per post)": "Coroană pe pivot (per pivot)",
  "Zirconium bridgework (per unit)": "Punte din zirconiu (per element)",
  "Single tooth E-max veneer (inc shade)": "Fațetă E-max pentru un dinte (include nuanța)",
  "E-max veneer": "Fațetă E-max",
  "Composite onlay/inlay": "Onlay/inlay compozit",
  "Ceramic onlay/inlay": "Onlay/inlay ceramic",

  // implants
  "Dental implant treatment planning": "Planificarea tratamentului cu implanturi dentare",
  "Dental implant placement": "Inserarea implantului dentar",
  "Implant crown": "Coroană pe implant",
  "Bone augmentation": "Augmentare osoasă",
  "Sinus lift": "Sinus lift",
  "Cementable implant temporary crown": "Coroană provizorie cimentabilă pe implant",
  "All-on-4 Same Day Teeth": "All-on-4 – dinți în aceeași zi",

  // cosmetics / other / dentures
  "Complex home tooth whitening Enlighten": "Albire dentară complexă la domiciliu Enlighten",
  "Night guard": "Gutieră de noapte",
  "Periodontal treatment": "Tratament parodontal",
  "Hygienist treatment": "Tratament la igienist",
  "Air Flow": "Air Flow",
  "Private full single arch": "Proteză totală privată, o arcadă",
  "Private full upper and lower": "Proteză totală privată, arcada superioară și inferioară",
  Reline: "Rebazare",
  "Addition of tooth": "Adăugare dinte",
  Repair: "Reparație",

  // orthodontics
  "A. Initial steps into orthodontic treatment": "A. Primii pași către tratamentul ortodontic",
  "New Patient Consultation": "Consultație pacient nou",
  "New Patient+ Adjustment": "Pacient nou + ajustare",
  "Orthodontic Records Package": "Pachet de documentație ortodontică",
  "Study models": "Modele de studiu",
  "B. Types of orthodontic treatments": "B. Tipuri de tratamente ortodontice",
  "Braces < 12 months (all inclusive)": "Aparat dentar < 12 luni (tot inclus)",
  "Braces 15–18 months (all inclusive)": "Aparat dentar 15–18 luni (tot inclus)",
  "Braces 24–30 months (all inclusive)": "Aparat dentar 24–30 luni (tot inclus)",
  "2. Clear Aligners — ALL INCLUSIVE": "2. Alignere transparente — TOT INCLUS",
  "Minor complexity": "Complexitate redusă",
  "Medium complexity": "Complexitate medie",
  Comprehensive: "Tratament complex",
  "3. Treatments for children": "3. Tratamente pentru copii",
  "Removable appliances, expanders — ALL INCLUSIVE": "Aparate mobile, expandere — TOT INCLUS",
  "C. Retention and Bracket Removal (Retainers)": "C. Contenție și îndepărtarea aparatului (retainere)",
  "Brace removal for non-EgoDent patients (single/dual arch)":
    "Îndepărtarea aparatului pentru pacienți din afara EgoDent (una/ambele arcade)",
  "Fixed retainers in-office, single arch": "Retainer fix realizat în cabinet, o arcadă",
  "Fixed retainers in-lab custom made, single arch": "Retainer fix personalizat din laborator, o arcadă",
  "Removable retainers (Essix) single arch": "Retainer mobil (Essix), o arcadă",

  // periodontal
  "Initial consultation": "Consultație inițială",
  "Deep cleaning (one session / price per arch)": "Detartraj în profunzime (o ședință / preț per arcadă)",
  "Closed debridement — Anterior (up to 6 teeth)": "Debridare închisă — frontal (până la 6 dinți)",
  "Closed debridement — Posterior (up to 6 teeth)": "Debridare închisă — lateral (până la 6 dinți)",
  "Helbo Photodynamic Therapy (per arch)": "Terapie fotodinamică Helbo (per arcadă)",
  "Flap Surgery — Anterior (up to 6 teeth)": "Chirurgie cu lambou — frontal (până la 6 dinți)",
  "Flap Surgery — Posterior (up to 6 teeth)": "Chirurgie cu lambou — lateral (până la 6 dinți)",
  "Single Tooth Recession (CTG)": "Recesie gingivală, un dinte (grefă de țesut conjunctiv – CTG)",
  "Multiple Recessions": "Recesii multiple",
  Frenectomy: "Frenectomie",
  "Crown Lengthening": "Alungire de coroană",
  "Other Surgeries (GTR)": "Alte intervenții chirurgicale (GTR)",
  "Reassessment (after 3 months)": "Reevaluare (după 3 luni)",
  "Supportive Periodontal Care": "Îngrijire parodontală de menținere",

  // medical
  "Gynaecological consultation": "Consultație ginecologică",
  "Obstetrical consultation": "Consultație obstetrică",
  "Transvaginal ultrasound": "Ecografie transvaginală",
  "Pregnancy diagnosis ultrasound": "Ecografie de diagnostic al sarcinii",
  "First-trimester ultrasound": "Ecografie de trimestrul I",
  "Gender reveal ultrasound": "Ecografie pentru stabilirea sexului fătului",
  "Fetal morphology ultrasound": "Ecografie morfologică fetală",
  "Third-trimester ultrasound": "Ecografie de trimestrul III",
  "3D/4D Ultrasound": "Ecografie 3D/4D",
  "Follow-up consultation": "Consultație de control",
};

function translatePrice(price: string): string {
  return price
    .replace(/^from /, "de la ")
    .replace(/^over /, "peste ")
    .replace(/^per tooth (£\d+)$/, "$1 / dinte")
    .replace(/^by assessment$/, "în urma evaluării");
}

const copy = {
  en: {
    dentalTitle: "Dental Price List",
    medicalTitle: "Medical Price List",
    sub: "Transparent fees — Stanmore & Walton-on-Thames Clinics",
    dentalTab: "Dental prices",
    medicalTab: "Medical prices",
    book: "Book Online",
    contact: "Contact Us",
    financeTitle: "Finance Options",
    financeText: "We offer finance options. Please ask a member of our team for more details.",
    disclaimer: "*subject to affordability, age and status, minimum spend applies",
    disclaimerOriginal: "",
    fees: "Fees may vary following clinical assessment. Where noted, prices may include laboratory work and clinical sessions.",
    catalogName: "EgoDent Dental & Medical Price List",
  },
  ro: {
    dentalTitle: "Listă de prețuri stomatologice",
    medicalTitle: "Listă de prețuri medicale",
    sub: "Tarife transparente — clinicile din Stanmore și Walton-on-Thames",
    dentalTab: "Prețuri stomatologie",
    medicalTab: "Prețuri medicale",
    book: "Programare online",
    contact: "Contactați-ne",
    financeTitle: "Opțiuni de finanțare",
    financeText: "Oferim opțiuni de finanțare. Vă rugăm să cereți detalii unui membru al echipei noastre.",
    disclaimer: "*în funcție de capacitatea de plată, vârstă și statut; se aplică o cheltuială minimă",
    disclaimerOriginal: "(subject to affordability, age and status, minimum spend applies)",
    fees: "Tarifele pot varia în urma evaluării clinice. Acolo unde este indicat, prețurile pot include lucrări de laborator și ședințe clinice.",
    catalogName: "Listă de prețuri EgoDent — stomatologie și medical",
  },
} as const;

/* ---------------- JSON-LD (Dental + Medical) ---------------- */

const offerCatalog = {
  "@context": "https://schema.org",
  "@type": "OfferCatalog",
  name: "EgoDent Dental & Medical Price List",
  url: "https://egodent.co.uk/price-list",
  itemListElement: [...dentalSections, ...medicalSections].map((s) => ({
    "@type": "OfferCatalog",
    name: s.title,
    itemListElement: (s.items || s.groups?.flatMap((g) => g.items) || []).map(
      ([name, price]) => ({
        "@type": "Offer",
        itemOffered: { "@type": "Service", name },
        priceSpecification: {
          "@type": "PriceSpecification",
          price,
          priceCurrency: "GBP",
        },
      })
    ),
  })),
};

export default function PriceListClient() {
  const locale = useLocale();
  const t = copy[locale];
  const tr = (s: string) => (locale === "ro" ? RO[s] ?? s : s);
  const trPrice = (p: string) => (locale === "ro" ? translatePrice(p) : p);
  const [active, setActive] = useState<"dental" | "medical">("dental");

  const catalog =
    locale === "ro"
      ? {
          ...offerCatalog,
          name: t.catalogName,
          url: "https://egodent.co.uk/ro/price-list",
          itemListElement: offerCatalog.itemListElement.map((s) => ({
            ...s,
            name: tr(s.name),
            itemListElement: s.itemListElement.map((o) => ({
              ...o,
              itemOffered: { ...o.itemOffered, name: tr(o.itemOffered.name) },
            })),
          })),
        }
      : offerCatalog;

  const activeSections = active === "dental" ? dentalSections : medicalSections;

  return (
    <>
      {/* HERO + TOGGLE */}
      <section className="bg-black text-center py-16">
        <h1 className="font-heading text-4xl md:text-5xl text-white">
          {active === "dental" ? t.dentalTitle : t.medicalTitle}
        </h1>
        <div className="mt-2 h-[2px] w-24 bg-[#27E8A7] rounded-full mx-auto" />
        <p className="text-[#27E8A7] mt-4 text-sm md:text-base">
          {t.sub}
        </p>

        <div className="mt-5 inline-flex rounded-full border border-[#27E8A7]/40 bg-black/70 p-1 shadow-[0_0_18px_rgba(39,232,167,0.3)]">
          <button
            type="button"
            onClick={() => setActive("dental")}
            className={`px-4 md:px-6 py-1.5 text-xs md:text-sm rounded-full transition-all ${
              active === "dental"
                ? "bg-[#27E8A7] text-black font-semibold shadow-[0_0_16px_rgba(39,232,167,0.7)]"
                : "text-white/70 hover:text-white"
            }`}
          >
            {t.dentalTab}
          </button>
          <button
            type="button"
            onClick={() => setActive("medical")}
            className={`px-4 md:px-6 py-1.5 text-xs md:text-sm rounded-full transition-all ${
              active === "medical"
                ? "bg-[#27E8A7] text-black font-semibold shadow-[0_0_16px_rgba(39,232,167,0.7)]"
                : "text-white/70 hover:text-white"
            }`}
          >
            {t.medicalTab}
          </button>
        </div>

        <div className="mt-6 flex items-center justify-center gap-3">
          <a
            href={`${locale === "ro" ? BOOK_URL_RO : BOOK_URL}?${UTM}`}
            target="_blank"
            rel="noopener"
            className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold bg-[#27E8A7] text-black hover:scale-[1.05] transition"
          >
            {t.book}
          </a>
          <Link
            href="/contact"
            className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold ring-1 ring-[#27E8A7]/40 text-[#27E8A7] hover:bg-[#27E8A7]/10 transition"
          >
            {t.contact}
          </Link>
        </div>
      </section>

      {/* LISTE */}
      <section className="container-px mx-auto py-12 max-w-5xl bg-black">
        {activeSections.map((section, idx) => (
          <div
            key={idx}
            className="mb-8 rounded-2xl border border-[#27E8A7]/25 bg-black/60 backdrop-blur-sm p-6 shadow-[0_0_20px_rgba(39,232,167,0.12)]"
          >
            <h2 className="text-2xl font-heading text-white">
              {tr(section.title)}
            </h2>
            <div className="mt-2 h-px w-16 bg-[#27E8A7]/70 rounded-full" />

            {section.items && (
              <table className="w-full border-collapse mt-4">
                <tbody>
                  {section.items.map(([name, price], i) => (
                    <tr
                      key={i}
                      className="border-b border-[#27E8A7]/15 text-sm sm:text-base"
                    >
                      <td className="py-2 pr-4 text-white/90">{tr(name)}</td>
                      <td className="py-2 text-right font-semibold text-[#27E8A7]">
                        {trPrice(price)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {section.groups &&
              section.groups.map((g, gi) => (
                <div key={gi} className="mt-5">
                  <h3 className="font-heading text-white/90">{tr(g.title)}</h3>
                  <table className="w-full border-collapse mt-2">
                    <tbody>
                      {g.items.map(([name, price], i) => (
                        <tr
                          key={i}
                          className="border-b border-[#27E8A7]/15 text-sm sm:text-base"
                        >
                          <td className="py-2 pr-4 text-white/90">{tr(name)}</td>
                          <td className="py-2 text-right font-semibold text-[#27E8A7]">
                            {trPrice(price)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
          </div>
        ))}

        <div className="text-center mt-12">
          <h2 className="text-2xl font-heading text-white mb-3">
            {t.financeTitle}
          </h2>
          <p className="text-white/70 max-w-2xl mx-auto text-sm md:text-base">
            {t.financeText}
          </p>
          <p className="text-[11px] text-white/50 mt-2">
            {t.disclaimer}
            {t.disclaimerOriginal && (
              <>
                {" "}
                <span lang="en">{t.disclaimerOriginal}</span>
              </>
            )}
          </p>
          <p className="text-[11px] text-white/50 mt-3">
            {t.fees}
          </p>
        </div>
      </section>

      {/* JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(catalog) }}
      />
    </>
  );
}
